// UBB-224 (CUST-011b) — GA4 guided-journey instrumentation helpers.
//
// Pure, DOM-free helpers shared by the two journey surfaces:
//   • src/js/entry/guided-journey.js  (/pages/build-bundle — steps 1–2)
//   • src/js/entry/loop-bundle.js     (Loop bundle page — steps 3–5,
//     add_bundle / remove_bundle, cart-attribute stamping)
//
// Everything here is exported for unit tests (tests/unit/journey-analytics.test.js).
// The dataLayer itself is initialised in layout/theme.liquid before GTM loads;
// getDataLayer() only re-creates it defensively.
//
// Event spec: UBB-224 dataLayer structure comment (Jira), amended by client
// feedback (2026-07: journey_step step-view name, no journey block or
// selling_plan on product events). Product event names are add_bundle /
// remove_bundle (TacticLab GA4 feedback — reverting the interim 2026-08-05
// product_selection / product_removal). GTM triggers are Custom Event triggers
// — the journey is SPA-like (single URL per surface).

export const ENTRY_POINT = 'build_bundle_page';

// GA4 product event names (TacticLab GA4 feedback — supersedes the interim
// 2026-08-05 product_selection / product_removal).
export const PRODUCT_EVENTS = {
  added: 'add_bundle',
  removed: 'remove_bundle',
};

// Canonical step registry. step_number is fixed per step name (funnel order),
// not the order a given session happens to visit them in.
export const JOURNEY_STEPS = {
  product_type: { number: 1, pathToken: 'product_type' },
  machine_selection: { number: 2, pathToken: 'machine' },
  plan_selection: { number: 3, pathToken: 'plan' },
  bundle_builder: { number: 4, pathToken: 'bundle' },
  add_ons: { number: 5, pathToken: 'add_ons' },
};

// sessionStorage key for the visited-steps path ('product_type>machine>…').
// sessionStorage (not localStorage) so the path is per-tab and per-session,
// surviving the /pages/build-bundle → Loop bundle page navigation.
export const PATH_STORAGE_KEY = 'ub_journey_path';

// Guided-journey controller states (src/js/entry/guided-journey.js STATES)
// → journey step names. Hero + selector are both the product-type choice;
// every Find My Pod panel state is machine identification.
const STATE_TO_STEP = {
  hero: 'product_type',
  selector: 'product_type',
  'panel-input': 'machine_selection',
  'panel-loading': 'machine_selection',
  'result-match': 'machine_selection',
  'result-none': 'machine_selection',
  contact: 'machine_selection',
  'contact-success': 'machine_selection',
  error: 'machine_selection',
};

/** Map a GuidedJourney state to its journey step name (null when unknown). */
export function mapStateToStep(state) {
  return STATE_TO_STEP[state] || null;
}

/** The page dataLayer (created if the theme.liquid init was somehow skipped). */
export function getDataLayer() {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/** journey_step payload for a known step name (null when unknown). */
export function buildStepViewPayload(stepName) {
  const step = JOURNEY_STEPS[stepName];
  if (!step) return null;
  return {
    event: 'journey_step',
    journey: {
      step_number: step.number,
      step_name: stepName,
      entry_point: ENTRY_POINT,
    },
  };
}

/**
 * add_bundle / remove_bundle payload. Callers must precede the push
 * with the reset push (pushProductEvent does both). No `journey` block —
 * per client feedback the step context already lives in the surrounding
 * journey_step events.
 *
 * @param {'add_bundle'|'remove_bundle'} eventName
 * @param {string} bundleId
 * @param {string} currency
 * @param {Array<object>} items - GA4 ecommerce items (buildEcommerceItem)
 */
export function buildProductEventPayload(eventName, bundleId, currency, items) {
  return {
    event: eventName,
    bundle_id: bundleId || '',
    ecommerce: {
      currency: currency || 'AUD',
      items: items || [],
    },
  };
}

/**
 * One GA4 ecommerce item from a selected-row snapshot + (optional) catalogue
 * variant info. Missing fields are omitted rather than sent empty, except the
 * identifiers GA4 needs to keep items distinct.
 */
export function buildEcommerceItem(row, variantInfo, options = {}) {
  const v = variantInfo || {};
  const item = {
    item_id: v.sku || row.sku || row.variantId || row.productId || '',
    item_variant_id: String(row.variantId || v.variantId || ''),
    item_name: v.productTitle || row.title || '',
    quantity: row.quantity,
  };
  const variantTitle = v.variantTitle || row.variantTitle;
  if (variantTitle) item.item_variant = variantTitle;
  if (options.category) item.item_category = options.category;
  const price = normalisePrice(v.price != null ? v.price : row.price);
  if (price != null) item.price = price;
  // No selling_plan: a non-standard item param that GA4 wouldn't ingest —
  // the client uses the presence of bundle_id as the subscription flag.
  return item;
}

/**
 * Normalise a price value that may arrive as a Money object ({ amount }), a
 * decimal string ('8.00'), a float, or integer cents (800). Integers >= 1000
 * are treated as cents (Shopify cart/variant JSON style) — this catalogue has
 * no whole-dollar prices >= $1000, verify in GA4 DebugView on live QA.
 */
export function normalisePrice(value) {
  if (value == null) return null;
  if (typeof value === 'object') {
    return value.amount != null ? Number(value.amount) : null;
  }
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value) && value >= 1000 ? value / 100 : value;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Step-view tracking
// ---------------------------------------------------------------------------

/**
 * Step-view firing with two dedupe modes:
 *  - default: consecutive dedupe — re-entering a step after leaving it fires
 *    again (guided-journey hero/selector/panel transitions)
 *  - { once: true }: at most once per tracker lifetime (Loop page, where all
 *    steps coexist on one screen)
 *
 * @param {(payload: object) => void} push
 */
export function createStepTracker(push) {
  let lastStep = null;
  const fired = new Set();
  return {
    track(stepName, options = {}) {
      const payload = buildStepViewPayload(stepName);
      if (!payload) return false;
      if (options.once && fired.has(stepName)) return false;
      if (!options.once && lastStep === stepName) return false;
      push(payload);
      lastStep = stepName;
      fired.add(stepName);
      return true;
    },
  };
}

// ---------------------------------------------------------------------------
// Journey path (sessionStorage-backed, storage injectable for tests)
// ---------------------------------------------------------------------------

/** Append a step's path token, skipping consecutive repeats. Returns new array. */
export function appendPathToken(tokens, stepName) {
  const step = JOURNEY_STEPS[stepName];
  const list = Array.isArray(tokens) ? tokens.slice() : [];
  if (!step) return list;
  if (list[list.length - 1] !== step.pathToken) list.push(step.pathToken);
  return list;
}

/** 'product_type>machine>plan>bundle>add_ons' */
export function formatJourneyPath(tokens) {
  return (Array.isArray(tokens) ? tokens : []).join('>');
}

/** sessionStorage, or null when unavailable (private mode / no window). */
export function getSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function readJourneyPath(storage) {
  if (!storage) return [];
  try {
    const raw = storage.getItem(PATH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Record a visited step into the stored path (no-op on storage failure). */
export function recordJourneyStep(storage, stepName) {
  const tokens = appendPathToken(readJourneyPath(storage), stepName);
  if (!storage) return tokens;
  try {
    storage.setItem(PATH_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // storage unavailable (private mode) — path degrades to partial
  }
  return tokens;
}

// ---------------------------------------------------------------------------
// Selection diffing (Loop bundle page)
// ---------------------------------------------------------------------------

/** Stable key for a selected-row snapshot (variant id when known). */
export function rowKey(row) {
  if (row.variantId) return `v:${row.variantId}`;
  return `p:${row.productId}|${row.variantTitle || row.title || ''}`;
}

/**
 * Diff two selection snapshots (arrays of row objects with quantity).
 * Returns { added, removed } — rows carrying the quantity DELTA, so a
 * 1 → 3 quantity change is a add_bundle with quantity 2.
 */
export function diffSelections(prev, next) {
  const prevMap = new Map((prev || []).map((r) => [rowKey(r), r]));
  const nextMap = new Map((next || []).map((r) => [rowKey(r), r]));
  const added = [];
  const removed = [];

  nextMap.forEach((row, key) => {
    const before = prevMap.get(key);
    const delta = row.quantity - (before ? before.quantity : 0);
    if (delta > 0) added.push({ ...row, quantity: delta });
    else if (delta < 0) removed.push({ ...row, quantity: -delta });
  });
  prevMap.forEach((row, key) => {
    if (!nextMap.has(key)) removed.push({ ...row });
  });

  return { added, removed };
}

// ---------------------------------------------------------------------------
// Catalogue lookup (__NEXT_DATA__) + bundle id resolution
// ---------------------------------------------------------------------------

/** '…/ProductVariant/47938954100962' | 47938954100962 → '47938954100962' */
export function normaliseId(value) {
  if (value == null) return '';
  const s = String(value);
  const m = s.match(/(\d+)\s*$/);
  return m ? m[1] : s;
}

/**
 * Index every product variant found under a Loop __NEXT_DATA__ bundle object
 * (shape not contractual — walked generically: any object with a `variants`
 * array is treated as a product). Returns { byVariantId, byProductId }.
 */
export function indexBundleVariants(bundle) {
  const byVariantId = new Map();
  const byProductId = new Map();
  const seen = new Set();

  const visit = (node, depth) => {
    if (!node || typeof node !== 'object' || depth > 8 || seen.has(node)) {
      return;
    }
    seen.add(node);
    if (Array.isArray(node)) {
      node.forEach((child) => visit(child, depth + 1));
      return;
    }
    if (Array.isArray(node.variants)) {
      const productId = normaliseId(node.id || node.productId);
      const productTitle = node.title || node.name || '';
      const infos = node.variants
        .filter((v) => v && typeof v === 'object')
        .map((v) => ({
          variantId: normaliseId(v.id || v.variantId),
          sku: v.sku || '',
          variantTitle: v.title || v.name || '',
          productId,
          productTitle,
          price: v.price != null ? v.price : v.priceAmount,
        }));
      infos.forEach((info) => {
        if (info.variantId) byVariantId.set(info.variantId, info);
      });
      if (productId) byProductId.set(productId, infos);
    }
    Object.keys(node).forEach((key) => visit(node[key], depth + 1));
  };

  visit(bundle, 0);
  return { byVariantId, byProductId };
}

/**
 * Best catalogue match for a selected-row snapshot: variant id when known,
 * else the product's variants (single variant, or title match).
 */
export function lookupVariant(index, row) {
  if (!index) return null;
  if (row.variantId) {
    const hit = index.byVariantId.get(normaliseId(row.variantId));
    if (hit) return hit;
  }
  const variants = index.byProductId.get(normaliseId(row.productId)) || [];
  if (variants.length === 1) return variants[0];
  if (row.variantTitle) {
    const match = variants.find((v) => v.variantTitle === row.variantTitle);
    if (match) return match;
  }
  return null;
}

/** Bundle template id from a Loop bundle URL path (fallback bundle_id). */
export function parseBundleIdFromPath(pathname) {
  const m = String(pathname || '').match(
    /loop_subscriptions\/bundle\/([A-Za-z0-9-]+)/i
  );
  return m ? m[1] : '';
}

/** First `_bundleId` line-item property in a /cart.js payload. */
export function extractCartBundleId(cart) {
  const items = (cart && cart.items) || [];
  for (const item of items) {
    const props = item && item.properties;
    if (props && props._bundleId) return String(props._bundleId);
  }
  return '';
}

/** /cart/update.js body stamping the journey context onto the cart. */
export function buildCartAttributesPayload(bundleId, pathTokens) {
  return {
    attributes: {
      journey_bundle_id: bundleId || '',
      journey_path: formatJourneyPath(pathTokens),
      journey_completed: 'true',
    },
  };
}

// ---------------------------------------------------------------------------
// Push helpers (thin DOM-facing layer)
// ---------------------------------------------------------------------------

/** Push a journey payload onto the page dataLayer (no-op without a window). */
export function pushToDataLayer(payload) {
  const dl = getDataLayer();
  if (dl && payload) dl.push(payload);
}

/**
 * Push a product event, preceded by the GA4-recommended ecommerce reset so
 * items from a previous event never leak into this one. `journey` is reset
 * too: GTM's data model persists it from the preceding journey_step push,
 * so without the explicit null the product event still reads as carrying
 * journey context (UBB-224 QA feedback 2026-08-05).
 */
export function pushProductEvent(payload) {
  if (!payload) return;
  pushToDataLayer({ ecommerce: null, journey: null });
  pushToDataLayer(payload);
}

/** Active storefront currency (AUD fallback). */
export function getCurrency() {
  if (typeof window !== 'undefined') {
    const shopify = window.Shopify;
    if (shopify && shopify.currency && shopify.currency.active) {
      return shopify.currency.active;
    }
  }
  return 'AUD';
}
